import {
  type ReactElement,
  type RefCallback,
  useCallback,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Area,
  AreaChart,
  ReferenceArea,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartContainer } from '@/components/charts/ChartContainer';
import { Loader } from '@/components/Loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWidgetHeaderSlot } from '@/sections/insights/metrics/context/WidgetHeaderSlot';
import { useWidgetData } from '@/sections/insights/metrics/hooks';
import { RISK_METRICS_CACHE } from '@/utils/staleWhileRevalidateCache.util';

import {
  computeAvailability,
  defaultWindow,
  gradeFromScore,
  pickPillIndices,
  sampleGrid,
  type SeriesPoint,
  type SnapshotRow,
  timeTicks,
  utcMsOf,
  WINDOW_DAYS,
  type WindowId,
} from './GradingHistoryZones.logic';

interface SSCRisk {
  status?: string;
  data?: { severity?: string; values?: { score?: number } };
  created?: string;
}

// Grade hue → chariot stroke token (bright, dual-theme; index.css:353-358,445-452).
// Used as SVG fill/stroke via CSS var. NO hex, NO raw Tailwind, NO dark: variants (brand contract).
const GRADE_TOKEN: Record<string, string> = {
  A: 'var(--color-chariot-stroke-light-green)', // index.css:357,449
  B: 'var(--color-chariot-stroke-lime)', // index.css lime token (grade B) — Q1 option (a)
  C: 'var(--color-chariot-stroke-medium)', // index.css:355,447
  D: 'var(--color-chariot-stroke-high)', // index.css:354,446
  F: 'var(--color-chariot-stroke-critical)', // index.css:353,445
};

// Per-grade pill TEXT ink (D4 — a11y >= 3:1 against the pill hue in BOTH themes).
// The pill fill is GRADE_TOKEN[grade], which is theme-dependent; the ink must therefore be
// theme-STABLE so the contrast holds without a dark: variant. The chariot
// background/text-primary tokens both FLIP between themes, so neither is usable here — we use
// the two static CSS keywords (black/white), which never theme-flip, per the brand-contract
// last-resort exception. Designer guidance = dark ink on the light hues (A/B/C), white on the
// dark hues (D/F). Measured WCAG contrast (verified, both themes; min over both shown):
//   A light-green: black 9.22 / 12.05  ✓   |  C medium:   black 9.99 / 12.79  ✓
//   B lime:        black 6.80 / 13.93  ✓   |  D high:      white 3.20 / 3.20   ✓ (orange)
//   F critical:    white 4.16 / 4.16   ✓ (red)
// All five clear 3:1 in both themes. (White on A/B/C would FAIL — e.g. B lime dark = 1.51 — so
// the split is required, not cosmetic.)
const PILL_INK: Record<string, string> = {
  A: 'black',
  B: 'black',
  C: 'black',
  D: 'white',
  F: 'white',
};

// Neutral line color for the Area (mock uses var(--line); --color-chariot-text-secondary is the
// existing neutral the gradient already uses). Replaces the old hard-coded gray line strokes.
// Must be the --color-chariot-* form (directly-usable color), NOT the raw RGB-triplet
// --chariot-* form (meant for rgb(var(...)) composition) — the triplet yields an invalid color.
const LINE_STROKE_TOKEN = 'var(--color-chariot-text-secondary)';

// Grade zone bands for the chart background — fills use the same chariot tokens (no hex).
const gradeZones = [
  { y1: 90, y2: 100, fill: GRADE_TOKEN.A, label: 'A' },
  { y1: 80, y2: 90, fill: GRADE_TOKEN.B, label: 'B' },
  { y1: 70, y2: 80, fill: GRADE_TOKEN.C, label: 'C' },
  { y1: 60, y2: 70, fill: GRADE_TOKEN.D, label: 'D' },
  { y1: 0, y2: 60, fill: GRADE_TOKEN.F, label: 'F' },
];

// Y-axis tick anchors at each grade band's CENTER score (not its bottom edge), so each letter
// reads vertically centered in its band. A[90,100]→95, B[80,90]→85, C[70,80]→75, D[60,70]→65,
// F[0,60]→30 (already the center). Exported as named constants so tests assert the actual wired
// values rather than string-matching the source.
/* eslint-disable react-refresh/only-export-components -- geometry constants exported for tests */
export const GRADE_AXIS_TICKS = [30, 65, 75, 85, 95] as const;
export const GRADE_TICK_MAP: Record<number, string> = {
  95: 'A',
  85: 'B',
  75: 'C',
  65: 'D',
  30: 'F',
};
/* eslint-enable react-refresh/only-export-components */

// Custom Y-axis tick that shows grade letters at boundaries
function GradeAxisTick({
  x,
  y,
  payload,
}: {
  x: number;
  y: number;
  payload: { value: number };
}) {
  const label = GRADE_TICK_MAP[payload.value];
  if (!label) return <g />;
  return (
    <text
      x={x - 4}
      y={y + 4}
      textAnchor="end"
      className="fill-chariot-text-secondary text-xs"
    >
      {label}
    </text>
  );
}

// --- Window labels for the header Select (rolling day-windows). ---
const WINDOW_LABEL: Record<WindowId, string> = {
  R1: 'Last 30 days',
  R3: 'Last 90 days',
  R12: 'Last 12 months',
};

// UTC-stable month names for the Tooltip date formatter (matches the logic-side formatter;
// NO formatDate — that formats in LOCAL tz and would shift a midnight-UTC boundary's day).
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

// Chart margins (kept from the original layout). innerWidth = container width minus the YAxis
// width and the horizontal plot margins; this is the single source of truth fed to both the
// pixel-gap pill decimation and the time-tick generation.
const CHART_MARGIN = { top: 38, right: 45, left: 30, bottom: 0 } as const;
const HORIZONTAL_MARGIN = CHART_MARGIN.left + CHART_MARGIN.right;
// Explicit YAxis width constant (was the inline <YAxis width={20}/>); subtracted from the
// measured container so innerWidth is the true plot-inner width (v2 follow-up fix).
const YAXIS_WIDTH = 20;
// Half-pill clearance padded into the XAxis so an edge pill (~73px wide → half ≈36.5) never
// clips at the domain ends. 40 >= max half-pill.
const PILL_HALF = 40;

// --- Pinned hook contract (HIGH-1): useWidgetData exposes processor output under `data`. ---

interface ProcessorOutput {
  rows: SnapshotRow[];
}
interface GradingData {
  rows: SnapshotRow[];
  isLoading: boolean;
}

// Daily snapshots only — NO synthetic 'Now' point, NO formatDate here. Aggregation and
// absolute date formatting happen downstream against the UTC date.
function processor(raw: SSCRisk[] | undefined): ProcessorOutput {
  const rows: SnapshotRow[] = (raw ?? [])
    .filter(s => s.data?.values?.score != null && s.created !== 'now')
    .map(s => ({ createdISO: s.created ?? '', score: s.data!.values!.score! }))
    .filter(r => r.createdISO !== '');
  return { rows };
}

function useGradingData(): GradingData {
  const { data, isLoading } = useWidgetData<SSCRisk[], ProcessorOutput>(
    'statistic',
    null,
    processor,
    {
      useStaticQuery: true,
      staticQuery: '#health-grade#risk-score#',
      cacheConfig: RISK_METRICS_CACHE,
    }
  );
  return { rows: data?.rows ?? [], isLoading };
}

// Measures the chart container's inner plotting width via ResizeObserver, mirroring
// ChartContainer's own measurement (it doesn't expose width to children). Returns 0
// until the container has settled — callers fall back to a sane default.
// Uses a callback ref (not useEffect) because the chart div mounts only after the
// loading/collecting branches early-return; an effect-attached observer would run
// before the div exists and never re-attach when it later mounts.
function useChartInnerWidth(): {
  ref: RefCallback<HTMLDivElement>;
  innerWidth: number;
} {
  const [width, setWidth] = useState(0);

  const ref = useCallback<RefCallback<HTMLDivElement>>(el => {
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setWidth(w);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // innerWidth = measured − YAxis width − left/right plot margins (true plot-inner width).
  return {
    ref,
    innerWidth: Math.max(0, width - YAXIS_WIDTH - HORIZONTAL_MARGIN),
  };
}

// Recharts dot render-prop payload (cx/cy are injected by Recharts; index lets us
// gate the grade-pill label by the budgeted index set computed once per render).
interface DotProps {
  cx: number;
  cy: number;
  index: number;
  payload: { Score: number };
}

// `plotLeft` (= CHART_MARGIN.left + YAXIS_WIDTH) MUST stay equal to the recharts plot clipPath x.
// recharts derives that clip x from the same two constants internally, in a separate place — if
// either constant changes, the clip x changes with it; keep this derivation in sync.
export const CLIP_SAFETY = 4; // keep the pill's left edge strictly inside the plot clip (no flush corner)

// Clamp the pill CENTER into the absolute-coordinate plot band so neither edge clips. The left
// bound adds CLIP_SAFETY so the pill's rounded-left corner clears the clipPath x rather than
// sitting flush against it; `hi` is floored at `lo` so a pill wider than the plot collapses to the
// left edge rather than inverting.
// eslint-disable-next-line react-refresh/only-export-components -- pure helper exported for tests
export function clampPillCenter(
  cx: number,
  half: number,
  plotLeft: number,
  innerWidth: number
): number {
  const lo = plotLeft + half + CLIP_SAFETY;
  const hi = Math.max(lo, plotLeft + innerWidth - half);
  return Math.max(lo, Math.min(cx, hi));
}

// Shared labeled-dot renderer (DRY): EVERY rendered point draws the grade-colored pill + ring
// (the decimation selector already dropped points that don't fit, so there are no bare dots).
// Dot radius/ring scale off the count of RENDERED points.
//
// Edge clamp (v3): Recharts passes `cx` in ABSOLUTE SVG coordinates — the plot's left edge is at
// `CHART_MARGIN.left + YAXIS_WIDTH`, NOT 0 — while `innerWidth` is the plot-INNER width. So the
// valid pill-center band in absolute space is [plotLeft + half, plotLeft + innerWidth − half].
// We clamp ONLY the pill rect/text center (`pillCx`) into that band; the circle stays at raw `cx`
// so the marker still sits on the true datum. The XAxis padding={{left,right}} already keeps the
// last/first dot off the frame in the normal case, so this clamp is defense-in-depth and must
// never push a pill further than the band edge.
function renderLabeledDot(
  props: DotProps,
  totalPoints: number,
  innerWidth: number
): ReactElement {
  const { cx, cy, index, payload } = props;
  const grade = gradeFromScore(payload.Score);
  const dotColor = GRADE_TOKEN[grade];
  const radius = totalPoints <= 16 ? 3.8 : 2.8; // density still scales; all labeled

  const label = `${grade} ${payload.Score.toFixed(1)}`;
  const pillWidth = label.length * 8.2 + 16;
  const pillHeight = 20;
  const half = pillWidth / 2;
  // Clamp pill center into the absolute-coordinate plot band (with a CLIP_SAFETY left margin) so
  // neither edge clips. The datum circle stays at raw `cx`; only the pill rect/text use `pillCx`.
  const plotLeft = CHART_MARGIN.left + YAXIS_WIDTH;
  const pillCx = clampPillCenter(cx, half, plotLeft, innerWidth);
  return (
    <g key={`dot-${index}`}>
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={dotColor}
        stroke="var(--color-chariot-background-primary)"
        strokeWidth={1.5}
      />
      <rect
        x={pillCx - half}
        y={cy - pillHeight - 8}
        width={pillWidth}
        height={pillHeight}
        rx={pillHeight / 2}
        fill={dotColor}
      />
      <text
        x={pillCx}
        y={cy - pillHeight / 2 - 8 + 1}
        textAnchor="middle"
        dominantBaseline="central"
        fill={PILL_INK[grade]}
        fontSize={11}
        fontWeight={700}
      >
        {label}
      </text>
    </g>
  );
}

// Shared trend chart (DRY): both the live widget and the Example render through this single
// component, so the time-axis + pill-decimation + tick policy lives in one place. The X axis is a
// TRUE numeric TIME axis over `ts` (UTC ms) with domain [t0,t1] = [now − WINDOW_DAYS, now], so the
// rendered geometry matches the window label (short history occupies only the right portion).
function TrendAreaChart({
  data,
  points,
  innerWidth,
  domain,
  windowId,
  gradientId,
}: {
  data: Array<{ ts: number; Score: number }>;
  points: SeriesPoint[];
  innerWidth: number;
  domain: [number, number];
  windowId: WindowId;
  gradientId: string;
}) {
  const [t0, t1] = domain;
  // Decimate to the points whose pills fit by pixel min-gap on the time scale; the chart's data
  // array becomes that subset, so every rendered dot is labeled by construction.
  const pillIdx = pickPillIndices(points, t0, t1, innerWidth);
  const renderedData = pillIdx.map(i => data[i]);
  const renderedPoints = pillIdx.map(i => points[i]);
  // One tick per sampled grid target (thinned to TICK_MIN_GAP, first+last always kept).
  const ticks = timeTicks(points, t0, t1, innerWidth, windowId);

  return (
    <AreaChart data={renderedData} margin={CHART_MARGIN}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop
            offset="0%"
            stopColor="var(--color-chariot-text-secondary)"
            stopOpacity={0.15}
          />
          <stop
            offset="100%"
            stopColor="var(--color-chariot-text-secondary)"
            stopOpacity={0.02}
          />
        </linearGradient>
      </defs>

      {/* Grade zone bands */}
      {gradeZones.map(zone => (
        <ReferenceArea
          key={zone.label}
          y1={zone.y1}
          y2={zone.y2}
          fill={zone.fill}
          fillOpacity={0.12}
          strokeOpacity={0}
        />
      ))}

      <XAxis
        type="number"
        dataKey="ts"
        domain={[t0, t1]}
        allowDataOverflow
        scale="linear"
        padding={{ left: PILL_HALF, right: PILL_HALF }}
        ticks={ticks.map(t => t.ts)}
        tickFormatter={(ts: number) =>
          ticks.find(t => t.ts === ts)?.label ?? ''
        }
        tickLine={false}
        axisLine={false}
        className="[&_tspan]:fill-chariot-text-secondary text-xs"
      />
      <YAxis
        domain={[0, 100]}
        ticks={[...GRADE_AXIS_TICKS]}
        interval={0}
        tick={GradeAxisTick}
        axisLine={false}
        tickLine={false}
        width={YAXIS_WIDTH}
      />
      <Tooltip
        contentStyle={{
          backgroundColor: 'var(--color-chariot-background-primary)',
          border: '1px solid var(--color-chariot-stroke)',
          borderRadius: '6px',
          fontSize: '12px',
        }}
        formatter={(value: number) => [
          `${value.toFixed(1)} (${gradeFromScore(value)})`,
          'Score',
        ]}
        labelFormatter={(ts: number) => {
          // ts is the XAxis datum (UTC ms of the bucket date). Format UTC-stable
          // "Mon D, YYYY" — NO formatDate (local tz would shift a midnight-UTC boundary's day).
          const d = new Date(ts);
          return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
        }}
      />
      <Area
        type="monotone"
        dataKey="Score"
        stroke={LINE_STROKE_TOKEN}
        strokeWidth={1.5}
        fill={`url(#${gradientId})`}
        // Disable the mount animation: its growing clipPath (0→full from the data origin) and
        // unsettled first-frame x-scale push the leftmost pill onto the clip edge, half-clipping it
        // until the animation settles. With it off, the pill lands at its clamped position on the
        // first paint. This disables the whole area/line/dots fade-in — intended and accepted.
        isAnimationActive={false}
        dot={(props: DotProps) =>
          renderLabeledDot(props, renderedPoints.length, innerWidth)
        }
        activeDot={{ r: 4 }}
      />
    </AreaChart>
  );
}

export function GradingHistoryZones() {
  const { rows, isLoading } = useGradingData();
  const { ref, innerWidth } = useChartInnerWidth();
  const headerSlot = useWidgetHeaderSlot();

  // Derived at render — no manual useMemo (MED-2; React Compiler memoizes). Sampling
  // ≤~400 daily rows is cheap. ONE impure clock read at the render boundary; everything
  // downstream is a pure function of `nowISO` (D-decision: no Date.now() inside .logic.ts).
  const nowISO = new Date().toISOString();
  // Day-aligned clock for the domain/coverage math (matches the grid's whole-UTC-day targets).
  const nowMs = utcMsOf(nowISO);
  const availability = computeAvailability(rows, nowISO);
  const [windowId, setWindowId] = useState<WindowId | null>(null);
  const effectiveWindow =
    windowId && availability[windowId].ok
      ? windowId
      : defaultWindow(availability);

  if (isLoading) {
    return <Loader type="spinner" isLoading={true} className="h-full" />;
  }

  // Collecting state: no window passes its rolling-window gate → message only (copy still
  // matches the R1 "2 weekly snapshots / ~2 weeks" gate).
  if (effectiveWindow === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1.5 px-10 text-center">
        <span className="text-chariot-text-primary text-sm font-semibold">
          Collecting weekly snapshots
        </span>
        <span className="text-chariot-text-secondary text-xs">
          Your perimeter score is recorded daily; the trend appears once 2
          weekly snapshots exist (about 2 weeks).
        </span>
      </div>
    );
  }

  const points = sampleGrid(rows, effectiveWindow, nowISO);
  // Chart data carries the REAL UTC ms (ts), not a formatted name — the X axis is a time scale.
  const data = points.map(p => ({ ts: p.ts, Score: p.score }));
  // Anchored time domain: exactly [now − WINDOW_DAYS, now], day-aligned (utcMsOf-based nowMs) so the
  // grid's populated endpoints sit ON the domain edges — the line spans edge to edge.
  const domain: [number, number] = [
    nowMs - WINDOW_DAYS[effectiveWindow] * 86_400_000,
    nowMs,
  ];
  // Width fallback before the container has measured (first paint / happy-dom):
  // 800px keeps all shipping windows unpruned.
  const effectiveInnerWidth = innerWidth > 0 ? innerWidth : 800;

  // Window-selection dropdown — portaled onto the widget's title row via the WidgetGrid header
  // slot. State (windowId/setWindowId) stays in this component, so the portaled Select and the
  // chart below share it automatically.
  const windowSelect = (
    <Select
      value={effectiveWindow}
      onValueChange={v => setWindowId(v as WindowId)}
    >
      <SelectTrigger className="w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(['R1', 'R3', 'R12'] as WindowId[]).map(id => (
          <SelectItem key={id} value={id} disabled={!availability[id].ok}>
            {WINDOW_LABEL[id]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <>
      {headerSlot && createPortal(windowSelect, headerSlot)}

      {/* Chart with grade-zone bands. The chart fills the full card height. */}
      <div ref={ref} className="relative h-full">
        <ChartContainer>
          <TrendAreaChart
            data={data}
            points={points}
            innerWidth={effectiveInnerWidth}
            domain={domain}
            windowId={effectiveWindow}
            gradientId="zonesGradient"
          />
        </ChartContainer>
      </div>
    </>
  );
}

// Static ~13-point mock so the Example exercises the SAME time-axis + decimation render path +
// tick policy as the live widget (DRY). Ascending ISO dates; ts stamped to match (grid targets).
const EXAMPLE_POINTS: SeriesPoint[] = [
  72, 74, 75, 73, 78, 80, 79, 82, 81, 84, 85, 83, 86,
].map((score, i) => {
  const day = i * 2 + 1;
  return {
    dateISO: `2026-01-${String(day).padStart(2, '0')}T00:00:00Z`,
    ts: Date.UTC(2026, 0, day),
    score,
  };
});

export function GradingHistoryZonesExample() {
  const data = EXAMPLE_POINTS.map(p => ({ ts: p.ts, Score: p.score }));
  // Synthetic time domain spanning the example's own range (first − 7d .. last) so the time-axis
  // path renders without depending on the real clock; windowId R3 picks fortnight ticks.
  const firstTs = EXAMPLE_POINTS[0].ts;
  const lastTs = EXAMPLE_POINTS[EXAMPLE_POINTS.length - 1].ts;
  const domain: [number, number] = [firstTs - 7 * 86_400_000, lastTs];

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <ChartContainer>
          <TrendAreaChart
            data={data}
            points={EXAMPLE_POINTS}
            innerWidth={800}
            domain={domain}
            windowId="R3"
            gradientId="zonesExGradient"
          />
        </ChartContainer>
      </div>
    </div>
  );
}
